import config from "../../config";
import { useGoogleMapsScript } from "../../useScript";
import { googlePlaceService } from "../utils/google/GooglePlaceService";
import { GooglePlaceLocationSearchFields } from "../utils/google/place.util";
import { placeToLocation } from "../utils/theme.util";

const MAX_CITY_RESULTS = 5;
const CITY_PLACE_TYPES = ["locality", "administrative_area_level_3"];

export type CitySearchResult = {
    city: string;
    country: string;
    countryCode: string;
};

export type CitySearch = {
    searchLocation: (query: string) => Promise<{ placeId: string; label: string }[]>;
    resolveLocation: (placeId: string) => Promise<CitySearchResult | null>;
};

export function useCitySearch(): CitySearch {
    const scriptStatus = useGoogleMapsScript();
    const ready = scriptStatus === "ready" && !!config.mapsApiKey;
    const service = googlePlaceService(config.mapsApiKey);

    async function searchLocation(query: string) {
        if (!ready) return [];
        const predictions = await service.findPlacePredictions(query, CITY_PLACE_TYPES);
        return predictions.slice(0, MAX_CITY_RESULTS);
    }

    async function resolveLocation(placeId: string) {
        if (!ready) return null;
        const place = await service.fetchGooglePlace(
            placeId,
            GooglePlaceLocationSearchFields,
        );
        const location = place ? placeToLocation(place) : null;
        if (!location?.city) return null;
        return {
            city: location.city,
            country: location.country,
            countryCode: location.countryCode,
        };
    }

    return { searchLocation, resolveLocation };
}
